<template>
  <div class="global-container">
    <CustomTable ref="table" class="compact-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
      <template v-slot:header-options>
        <div class="filters">
          <button v-for="mode in PAYMENT_MODES" :key="mode.value" class="filter-button" :class="{ active: isFilterActive('paymentType', mode.value) }"
                  :title="'Afficher uniquement les décaissements : ' + mode.label.toLowerCase()" @click="toggleFilter('paymentType', mode.value)">
            <span :class="['fas', mode.icon]"></span>{{ mode.label }}
          </button>
        </div>
        <button @click="exportToExcel" class="action-button ghost only-desktop-flex" type="button" title="Exporter les décaissements affichés (Excel)"><span class="fas fa-download"></span>Exporter</button>
      </template>
      <template v-slot:table>
        <thead>
        <tr>
          <custom-header class="col-fit" field="date">Date</custom-header>
          <custom-header class="col-fit" field="supplierName" :sortable="false">Fournisseur</custom-header>
          <custom-header class="col-fit" field="purchaseReference" :sortable="false">Pièce</custom-header>
          <custom-header field="purchaseLabel" :sortable="false">Achat</custom-header>
          <custom-header class="col-fit" field="paymentType">Mode</custom-header>
          <custom-header class="col-fit hide-for-mobile" field="journal" :sortable="false">Journal</custom-header>
          <custom-header class="col-fit hide-for-mobile" field="reference">Référence</custom-header>
          <custom-header class="col-fit right" field="amount">Montant</custom-header>
        </tr>
        </thead>
        <tbody v-if="data && data.length > 0">
        <tr v-if="resumeData" class="total compact-row">
          <td>Total ({{ resumeData.count }})</td>
          <td colspan="6"></td>
          <td class="right">{{ money(resumeData.total) }}</td>
        </tr>
        <tr v-for="disbursement in $refs.table.$refs.searchArray.simpleFilter(data)" :key="disbursement.id" class="compact-row" :class="{editable: !isAccountantModule}" :title="isAccountantModule ? undefined : 'Modifier le décaissement'" @click="openDisbursementModal(disbursement)">
          <custom-body class="col-fit" field="date">{{ displayDate(dateToFec(disbursement.date)) }}</custom-body>
          <custom-body class="col-fit col-truncate" field="supplierName" :title="disbursement.supplierName || '-'">{{ disbursement.supplierName || '-' }}</custom-body>
          <custom-body class="col-fit" field="purchaseReference"><span class="doc-chip">{{ disbursement.purchaseReference || '-' }}</span></custom-body>
          <custom-body field="purchaseLabel"><span v-if="disbursement.isRefund" class="doc-chip" title="Remboursement d'un avoir fournisseur">Remboursement</span> {{ disbursement.purchaseLabel }}</custom-body>
          <custom-body class="col-fit" field="paymentType">{{ paymentTypeLabel(disbursement.paymentType) }}</custom-body>
          <custom-body class="col-fit hide-for-mobile" field="journal">{{ journalOf(disbursement) }}</custom-body>
          <custom-body class="col-fit hide-for-mobile col-truncate" field="reference" :title="disbursement.reference || '-'">{{ disbursement.reference || '-' }}</custom-body>
          <custom-body class="col-fit right" field="amount">{{ money(signed(disbursement)) }}</custom-body>
        </tr>
        </tbody>
        <tbody v-else><tr class="empty-row"><td colspan="8">Aucun décaissement enregistré</td></tr></tbody>
      </template>
    </CustomTable>
    <DisbursementModal ref="disbursementModal" :callback="reload" />
  </div>
</template>

<script>
import CustomTable from "@/components/CustomTable.vue";
import {library} from '@fortawesome/fontawesome-svg-core';
import {faBuildingColumns, faCoins, faCreditCard, faDownload, faMoneyCheck, faRotate} from "@fortawesome/free-solid-svg-icons";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import DisbursementModal from "@/purchase/components/modals/DisbursementModal.vue";
import {AccountingController} from "@/accounting/controllers/AccountingController";
import {AccountingTableMixin} from "@/accounting/services/accountingTableMixin";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {AccountingService} from "@/accounting/services/AccountingService";
import {dateToFec, displayDate, excelDate, money, paymentTypeLabel} from "@/accounting/services/export/AccountingFormat";
import {DATE_FORMAT, MONEY_FORMAT} from "@/accounting/services/export/ExcelTable";
library.add(faBuildingColumns, faCoins, faCreditCard, faDownload, faMoneyCheck, faRotate);

const PAYMENT_MODES = [
  {value: 'TRANSFER', label: paymentTypeLabel('TRANSFER'), icon: 'fa-building-columns'},
  {value: 'CHECK', label: paymentTypeLabel('CHECK'), icon: 'fa-money-check'},
  {value: 'CARD', label: paymentTypeLabel('CARD'), icon: 'fa-credit-card'},
  {value: 'DIRECT_DEBIT', label: paymentTypeLabel('DIRECT_DEBIT'), icon: 'fa-rotate'},
  {value: 'CASH', label: paymentTypeLabel('CASH'), icon: 'fa-coins'},
];

const DATE_FIELD = "date";

/**
 * Décaissements de la période (paiements des achats), avec leur fournisseur, leur achat et le journal de trésorerie
 * dans lequel ils sont comptabilisés (BQ banque, CA caisse pour les espèces).
 * Le remboursement d'un avoir fournisseur est une entrée d'argent : il s'affiche en négatif.
 */
export default {
  name: 'DisbursementForAccountingTable',
  mixins: [AccountingTableMixin],
  components: {DisbursementModal, CustomBody, CustomHeader, CustomTable},
  data: function () {
    return {
      PAYMENT_MODES,
      dateField: DATE_FIELD,
      data: [],
      resumeData: undefined,
      elements: [
        {label: "Date", value: "date", type: "date"},
        {label: "Mode", value: "paymentType", type: "text"},
        {label: "Référence", value: "reference", type: "text"},
        {label: "Montant", value: "amount", type: "number"},
      ],
      searchOptions: {
        filters: AccountingService.periodFilters(DATE_FIELD),
        sorts: [{field: DATE_FIELD, order: SORT_OPERATOR_ENUM.ASCENDING.value}]
      },
    }
  },
  methods: {
    displayDate,
    dateToFec,
    money,
    paymentTypeLabel,
    signed(disbursement) {
      return disbursement.isRefund ? -disbursement.amount : disbursement.amount;
    },
    // Journal de l'écriture du décaissement : les espèces passent par la caisse, tout le reste par la banque
    journalOf(disbursement) {
      return disbursement.paymentType === 'CASH' ? 'CA · Caisse' : 'BQ · Banque';
    },
    // Le module comptable consulte les décaissements des professionnels : pas de modification
    openDisbursementModal(disbursement) {
      if (this.isAccountantModule) return;
      this.$refs.disbursementModal.openModal({disbursement});
    },
    requestTable(urlParams, professionalId) {
      return AccountingController.getDisbursementTable(urlParams, professionalId);
    },
    requestResume(urlParams, professionalId) {
      return AccountingController.getDisbursementResume(urlParams, professionalId);
    },
    exportToExcel() {
      return this.downloadExcel('Décaissements', [
        {header: 'Date', pick: row => excelDate(dateToFec(row.date)), format: DATE_FORMAT},
        {header: 'Fournisseur', pick: row => row.supplierName},
        {header: 'Pièce', pick: row => row.purchaseReference},
        {header: 'Achat', pick: row => row.purchaseLabel},
        {header: 'Mode', pick: row => paymentTypeLabel(row.paymentType)},
        {header: 'Journal', pick: row => this.journalOf(row)},
        {header: 'Référence', pick: row => row.reference},
        {header: 'Montant', pick: row => this.signed(row), format: MONEY_FORMAT, total: true},
      ]);
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';
@require '~/src/accounting/style/accountingTable.styl';
</style>
